import { authenticatedFetch, getAuthHeaders } from './authApi';
import { getCachedOrFetch, invalidateCachedValue } from './requestCache';
import { getEnv } from '../utils/env';

type UnknownRecord = Record<string, unknown>;

const toApiBaseUrl = (): string => getEnv('VITE_AI_API_URL', '').replace(/\/$/, '');
const TEAMS_TTL_MS = 30_000;

const parseJson = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const extractErrorMessage = (payload: unknown, fallback: string): string => {
  if (!payload || typeof payload !== 'object') return fallback;
  if ('detail' in payload && typeof (payload as UnknownRecord).detail === 'string') return (payload as UnknownRecord).detail as string;
  if ('message' in payload && typeof (payload as UnknownRecord).message === 'string') {
    return (payload as UnknownRecord).message as string;
  }
  return fallback;
};

const requestTeamsApi = async (path: string, options: RequestInit = {}): Promise<unknown> => {
  const baseUrl = toApiBaseUrl();
  if (!baseUrl) {
    throw new Error('VITE_AI_API_URL is not configured');
  }

  const response = await authenticatedFetch(`${baseUrl}${path}`, {
    ...options,
    credentials: 'include',
    headers: getAuthHeaders(options.headers || {}),
  });

  const payload = await parseJson(response);
  if (!response.ok) {
    throw new Error(extractErrorMessage(payload, `Teams API request failed (${response.status})`));
  }

  return payload;
};

const asRecord = (value: unknown): UnknownRecord | null =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as UnknownRecord) : null;

const extractList = (payload: unknown): unknown[] => {
  if (Array.isArray(payload)) return payload;
  const record = asRecord(payload);
  if (!record) return [];
  if (Array.isArray(record.teams)) return record.teams as unknown[];
  if (Array.isArray(record.data)) return record.data as unknown[];
  return [];
};

const extractItem = (payload: unknown): unknown => {
  const record = asRecord(payload);
  if (!record) return payload;
  if (record.team) return record.team;
  if (record.data) return record.data;
  return payload;
};

export const listTeamsApi = async (seasonId?: string): Promise<unknown[]> => {
  const query = seasonId ? `?season_id=${encodeURIComponent(seasonId)}` : '';
  return await getCachedOrFetch(`teams:list:${seasonId || 'all'}`, TEAMS_TTL_MS, async () => {
    const payload = await requestTeamsApi(`/api/v1/teams${query}`, { method: 'GET' });
    return extractList(payload);
  });
};

export const createTeamApi = async (payload: Record<string, unknown>): Promise<unknown> => {
  const responsePayload = await requestTeamsApi('/api/v1/teams', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  invalidateCachedValue('teams:');
  return extractItem(responsePayload);
};

export const getTeamApi = async (teamId: string): Promise<unknown | null> => {
  return await getCachedOrFetch(`teams:get:${teamId}`, TEAMS_TTL_MS, async () => {
    const payload = await requestTeamsApi(`/api/v1/teams/${encodeURIComponent(teamId)}`, {
      method: 'GET',
    });
    return extractItem(payload) ?? null;
  });
};

export const updateTeamApi = async (teamId: string, payload: Record<string, unknown>): Promise<unknown> => {
  const responsePayload = await requestTeamsApi(`/api/v1/teams/${encodeURIComponent(teamId)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });
  invalidateCachedValue('teams:list');
  invalidateCachedValue(`teams:get:${teamId}`);
  invalidateCachedValue(`players:list:preparation:${teamId}:`);
  return extractItem(responsePayload);
};

export const deleteTeamApi = async (teamId: string): Promise<void> => {
  await requestTeamsApi(`/api/v1/teams/${encodeURIComponent(teamId)}`, {
    method: 'DELETE',
  });
  invalidateCachedValue('teams:list');
  invalidateCachedValue(`teams:get:${teamId}`);
  invalidateCachedValue(`players:list:preparation:${teamId}:`);
};

export interface RolloverOptions {
  players: { id: string; name: string }[];
  coaches: { id: string; name: string }[];
  selected_players: string[];
  selected_standins: string[];
  selected_coaches: string[];
}

export interface TeamRolloverInput {
  name: string;
  season_id: string;
  players: string[];
  standins: string[];
  user_ids: string[];
  default_formation_key: string | null;
  default_indoor_formation_key?: string | null;
  playing_environment?: 'indoor' | 'outdoor' | 'both';
}

export const getTeamRolloverOptionsApi = async (id: string): Promise<RolloverOptions> => {
  const payload = await requestTeamsApi(`/api/v1/teams/${encodeURIComponent(id)}/rollover-options`);
  return (payload as { data: RolloverOptions }).data;
};

export const rolloverTeamApi = async (id: string, input: TeamRolloverInput): Promise<{ id: string }> => {
  const payload = await requestTeamsApi(`/api/v1/teams/${encodeURIComponent(id)}/rollover`, {
    method: 'POST', body: JSON.stringify(input),
  });
  invalidateCachedValue('teams:');
  return extractItem(payload) as { id: string };
};


export const downloadTeamPdfApi = async (teamId: string, locale: string, formationKey?: string, playerIds?: string[]): Promise<Blob> => {
  const query = new URLSearchParams({ locale });
  if (formationKey) query.set('formation_key', formationKey);
  if (playerIds) { query.set('section', 'statistics'); query.set('player_ids', playerIds.join(',')); }
  const response = await authenticatedFetch(`${toApiBaseUrl()}/api/v1/teams/${encodeURIComponent(teamId)}/pdf?${query}`, {
    credentials: 'include', headers: getAuthHeaders(),
  });
  if (!response.ok || !response.headers.get('content-type')?.includes('application/pdf')) {
    throw new Error('Team PDF request failed');
  }
  return response.blob();
};
